import eventBus from '@/eventBus'
import axios from '../../axios'

const POPUP_POLL_MS = 500

// Deliberately generous: a real SSO login can involve an account chooser, typing a password and
// waiting on 2FA, so this is a last-resort backstop against a promise that would otherwise never
// settle, not a UX deadline. The popup.closed poll below catches the realistic failures long
// before this fires.
const SSO_TIMEOUT_MS = 10 * 60 * 1000

const onError = e => {
	console.error(e)
	eventBus.$emit('flashMessage', {
		message: "Oops, we weren't able to use that login. Please try again",
	})
}

// Each attempt opens its own window (unique name below), so a callback can only ever settle the
// attempt that opened that window: there is no shared window, and no way for one callback to settle
// two promises. Clicking a second provider closes the previous attempt's window and rejects it
// quietly (switching providers is not a failure) before arming the new one. This handle is how a new
// attempt reaches the previous one to do that.
let cancelActiveAttempt = null

// A fresh window name per attempt. `window.open` reuses an existing browsing context when the name
// is already taken, and a new attempt opens its window *before* closing the previous one (so a
// blocked open leaves a still-working attempt alone), meaning the two briefly coexist. A shared name
// would hand the second attempt the first's window; distinct names keep them separate.
let windowSeq = 0

export const initiateSso = type => {
	const { promise, resolve, reject } = Promise.withResolvers()

	// Opened synchronously, before any await, while the click's transient user activation is still
	// live: opening it once the state-uuid request resolves is too late (Safari blocks the popup
	// outright, Firefox blocks it whenever that request is slow). The cost is a blank window for the
	// length of the request.
	const popup = window.open('', `momo-extension-sso-login-${(windowSeq += 1)}`, 'popup')
	// No window to log in with, so don't mint a state UUID for a login that can't run, and leave any
	// attempt already in flight untouched.
	if (!popup) {
		onError('Could not login using sso. the login window was blocked')
		reject('Could not login using sso. the login window was blocked')
		return promise
	}

	let teardown = () => {}
	let done = false

	const finish = () => {
		done = true
		teardown()
		// Clear the active-attempt handle. By the time any attempt reaches finish, this handle can only
		// point at that same attempt: a replacement runs the old cancel before reassigning, and every
		// other settle path is `done`-guarded, so an attempt never finishes after a successor took over.
		// That is why the clear needs no `=== thisCancel` check.
		cancelActiveAttempt = null
	}

	const fail = reason => {
		// `reject` on a settled promise is a no-op, but the side effects below are not, so guard them:
		// a replaced attempt whose state-uuid request fails afterwards must not flash an error or close
		// a window the newer attempt is now using.
		if (done) return
		finish()
		// Nowhere left to navigate it, so don't leave a blank window on screen.
		popup.close()
		onError(reason)
		reject(reason)
	}

	// Now that we have our own window, retire any previous attempt: close its window and reject it.
	// Replacing an attempt (the user clicked another provider) is not a user-visible failure, so this
	// rejects without a flash; tearing down first stops the poll, so closing the window does not also
	// trip the "login window was closed" path. Done after the open-and-block check above, so a blocked
	// second click does not kill a first attempt the user may still complete.
	cancelActiveAttempt?.()
	cancelActiveAttempt = () => {
		finish()
		popup.close()
		reject('Could not login using sso. a newer login attempt replaced this one')
	}

	let marketingOrigin
	try {
		marketingOrigin = new URL(process.env.VUE_APP_MARKETING_URI).origin
	} catch (reason) {
		fail(reason)
		return promise
	}

	// Prod serves the marketing site at both apex and www (momentumdash.com and www.momentumdash.com),
	// and the SSO callback can post from a different one than we navigated to. Accept the www/apex
	// sibling of the configured marketing origin, but nothing else: same protocol and port, and a host
	// that only differs by a leading `www.` (subdomains and lookalikes are still rejected).
	// Keep in sync with marketing-site's `isSameSite` (originAllowlist.js) — the same check, other side.
	const isMarketingOrigin = origin => {
		try {
			const bareHost = h => h.replace(/^www\./, '')
			const a = new URL(origin)
			const b = new URL(marketingOrigin)
			return a.protocol === b.protocol && a.port === b.port && bareHost(a.hostname) === bareHost(b.hostname)
		} catch {
			return false
		}
	}

	const handler = message => {
		// Only accept the SSO callback from the login window we opened, on the marketing origin.
		// Without this, any page holding a handle to this one can post a forged `ssoResponseCallback`
		// and log the user into an account that isn't theirs.
		if (message.source !== popup || !isMarketingOrigin(message.origin)) return
		if (!message.data || typeof message.data !== 'object' || !('ssoResponseCallback' in message.data)) {
			return
		}

		const callback = message.data.ssoResponseCallback
		if (callback && typeof callback === 'object' && 'otp' in callback) {
			// We own the popup now: the marketing callback page deliberately no longer closes itself,
			// because closing before this handler runs nulls event.source and fails the `source === popup`
			// guard above. Tear down (stopping the closed-window poll), then close it here.
			finish()
			popup.close()
			resolve(callback)
		} else {
			fail('Could not login using sso. no OTL parameters received')
		}
	}

	// `closed` is one of the few properties still readable across origins, so polling it is legitimate.
	// What it actually catches: the user closing the window, and the blank window being dismissed during
	// a slow state-uuid request. (An *enforcing* Cross-Origin-Opener-Policy swap would also surface as
	// `closed === true`, but the header in play today is report-only, so that case does not fire here.)
	// Armed from the moment the window exists, not the navigation, so closing the blank window during a
	// slow request rejects instead of hanging on the timeout.
	const poll = setInterval(() => {
		if (popup.closed) fail('Could not login using sso. the login window was closed')
	}, POPUP_POLL_MS)
	const timeout = setTimeout(() => fail('Could not login using sso. the login timed out'), SSO_TIMEOUT_MS)

	teardown = () => {
		clearInterval(poll)
		clearTimeout(timeout)
		window.removeEventListener('message', handler)
	}
	window.addEventListener('message', handler)

	axios
		.post('user/sso-state-uuid', {
			name: null, // will take returned value from sso provider
			source: 'account',
			sourceUri: location.origin,
		})
		.then(({ data }) => {
			// Settled while the request was in flight (replaced, failed or timed out): our window has
			// already been closed by whichever path settled us, so there is nothing to navigate.
			if (done) return

			// Setting `location` is permitted cross-origin on a window we opened, and it keeps the same
			// WindowProxy, so the `message.source !== popup` guard still holds.
			popup.location = `${process.env.VUE_APP_MARKETING_URI}sso/${type}?state=${data.id}`
		})
		.catch(fail)

	return promise
}
